import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import {
  createMatchHubConnection,
  MatchLiveState,
  MatchEndedPayload,
  remainingMsFromSnapshot,
  osaekomiSecondsFromSnapshot,
} from '../../utils/matchHub';
import { fetchMatIdleView, MatIdleView } from '../../utils/matDisplayApi';
import MatIdleContent from './MatIdleContent';
import './MatDisplay.scss';

// Публичная страница табло: открывается один раз на мониторе над конкретным ковром.
// Живой бой всегда перекрывает простой. Контекст между боями задаёт админ в /admin/displays.

type Mode = 'idle' | 'live' | 'ended';

const formatTime = (ms: number) => {
  const clamped = Math.max(0, Number.isFinite(ms) ? ms : 0);
  const minutes = Math.floor(clamped / 60000);
  const seconds = Math.floor((clamped % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const MatDisplay: React.FC = () => {
  const { matNumber } = useParams<{ matNumber: string }>();

  const [mode, setMode] = useState<Mode>('idle');
  const [liveState, setLiveState] = useState<MatchLiveState | null>(null);
  const [endedInfo, setEndedInfo] = useState<MatchEndedPayload | null>(null);
  const [idleView, setIdleView] = useState<MatIdleView | null>(null);
  const [connectionState, setConnectionState] = useState<signalR.HubConnectionState>(
    signalR.HubConnectionState.Connecting
  );

  const [tick, setTick] = useState(() => performance.now());
  const [liveReceivedAt, setLiveReceivedAt] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(performance.now()), 250);
    return () => clearInterval(id);
  }, []);

  const endedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveStateRef = useRef<MatchLiveState | null>(null);
  const preferIdleUntilRef = useRef(0);
  liveStateRef.current = liveState;

  const loadIdleView = useCallback(async () => {
    const n = Number(matNumber);
    if (!Number.isFinite(n) || n < 1) return;
    try {
      setIdleView(await fetchMatIdleView(n));
    } catch {
      setIdleView(null);
    }
  }, [matNumber]);

  useEffect(() => {
    if (!matNumber) return;

    const connection = createMatchHubConnection();

    connection.on('MatchStateUpdated', (state: MatchLiveState) => {
      if (endedTimeoutRef.current) {
        clearTimeout(endedTimeoutRef.current);
        endedTimeoutRef.current = null;
      }
      // Админ только что выбрал простой — не перекрываем его парой, которая ещё не стартовала.
      if (!state.isStarted && Date.now() < preferIdleUntilRef.current) {
        return;
      }
      const receivedAt = performance.now();
      setLiveReceivedAt(receivedAt);
      setTick(receivedAt);
      setLiveState(state);
      setEndedInfo(null);
      setMode('live');
    });

    connection.on('MatchEnded', (payload: MatchEndedPayload) => {
      setEndedInfo(payload);
      setMode('ended');
    });

    connection.on('MatIdle', () => {
      if (endedTimeoutRef.current) {
        clearTimeout(endedTimeoutRef.current);
        endedTimeoutRef.current = null;
      }
      setMode('idle');
      setLiveState(null);
      loadIdleView();
    });

    connection.on('IdleContextUpdated', () => {
      loadIdleView();
      const live = liveStateRef.current;
      // Пока бой идёт, админские настройки простоя не должны сбрасывать табло.
      if (live?.isStarted) return;
      preferIdleUntilRef.current = Date.now() + 4000;
      setMode('idle');
      setLiveState(null);
      setEndedInfo(null);
    });

    const join = () => {
      connection.invoke('JoinMat', matNumber)
        .then(() => connection.invoke('RequestState', matNumber))
        .catch(err => console.error('[табло] не удалось подписаться на ковёр', err));
    };

    connection.onreconnecting(() => setConnectionState(signalR.HubConnectionState.Reconnecting));
    connection.onreconnected(() => {
      setConnectionState(signalR.HubConnectionState.Connected);
      join();
      loadIdleView();
    });
    connection.onclose(() => setConnectionState(signalR.HubConnectionState.Disconnected));

    connection.start()
      .then(() => {
        setConnectionState(signalR.HubConnectionState.Connected);
        join();
      })
      .catch(err => {
        console.error('[табло] не удалось подключиться к хабу', err);
        setConnectionState(signalR.HubConnectionState.Disconnected);
      });

    loadIdleView();

    return () => {
      if (endedTimeoutRef.current) clearTimeout(endedTimeoutRef.current);
      connection.stop();
    };
  }, [matNumber, loadIdleView]);

  const remainingMs = useMemo(() => {
    if (!liveState) return 0;
    return remainingMsFromSnapshot(liveState, liveReceivedAt, tick);
  }, [liveState, liveReceivedAt, tick]);

  const osaekomiSeconds = useMemo(() => {
    if (!liveState) return 0;
    return osaekomiSecondsFromSnapshot(liveState, liveReceivedAt, tick);
  }, [liveState, liveReceivedAt, tick]);

  return (
    <div className={`mat-display${mode === 'idle' ? ' mat-display--idle' : ''}`}>
      <div className="mat-display__mat-label">Ковёр №{matNumber}</div>

      {connectionState !== signalR.HubConnectionState.Connected && (
        <div className="mat-display__connection-warning">Нет связи с сервером...</div>
      )}

      {mode === 'live' && liveState && (
        <div className="mat-display__live">
          <div className={`mat-display__timer ${liveState.osaekomiSide ? 'osaekomi-active' : ''} ${liveState.isGoldenScore ? 'golden-score' : ''} ${liveState.isHantei ? 'hantei' : ''}`}>
            {liveState.isGoldenScore && <div className="mat-display__phase">Голден скор</div>}
            {liveState.isHantei && <div className="mat-display__phase">Хантей</div>}
            {formatTime(remainingMs)}
            {liveState.osaekomiSide && (
              <div className="mat-display__osaekomi">
                УДЕРЖАНИЕ ({liveState.osaekomiSide === 'home' ? liveState.homeName : liveState.awayName}): {osaekomiSeconds}с
              </div>
            )}
          </div>

          <div className="mat-display__participants">
            <div className="mat-display__participant">
              <div className="mat-display__name">{liveState.homeName}</div>
              <div className="mat-display__scores">
                <div><span>{liveState.homeIppon}</span><label>Иппон</label></div>
                <div><span>{liveState.homeWazaAri}</span><label>Вазари</label></div>
                <div><span>{liveState.homeYuko}</span><label>Юко</label></div>
                <div className="shido"><span>{liveState.homeShido}</span><label>Шидо</label></div>
              </div>
            </div>

            <div className="mat-display__vs">vs</div>

            <div className="mat-display__participant">
              <div className="mat-display__name">{liveState.awayName}</div>
              <div className="mat-display__scores">
                <div><span>{liveState.awayIppon}</span><label>Иппон</label></div>
                <div><span>{liveState.awayWazaAri}</span><label>Вазари</label></div>
                <div><span>{liveState.awayYuko}</span><label>Юко</label></div>
                <div className="shido"><span>{liveState.awayShido}</span><label>Шидо</label></div>
              </div>
            </div>
          </div>

          {!liveState.isStarted && <div className="mat-display__hint">Поединок скоро начнётся</div>}
          {liveState.isStarted && liveState.isPaused && <div className="mat-display__hint">Пауза</div>}
          {liveState.isHantei && <div className="mat-display__hint">Судья выбирает победителя</div>}
        </div>
      )}

      {mode === 'ended' && endedInfo && (
        <div className="mat-display__ended">
          <div className="mat-display__ended-label">Поединок завершён</div>
          {liveState && (
            <div className="mat-display__ended-pair">
              {liveState.homeName} — {liveState.awayName}
            </div>
          )}
          <div className="mat-display__ended-winner">
            {endedInfo.winnerName === 'Победитель' ? 'Победитель' : `Победитель ${endedInfo.winnerName}`}
          </div>
        </div>
      )}

      {mode === 'idle' && (
        <div className="mat-display__idle">
          <MatIdleContent view={idleView} />
        </div>
      )}
    </div>
  );
};

export default MatDisplay;
